import cx from 'classnames';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { playButtonSound } from '@wg/web2clientapi/sound';
import ProgressBar from '@wg/wows-react-uikit/components/ProgressBar/ProgressBar';
import Guidingtip, {
  GUIDINGTIP_POSITIONS,
  GUIDINGTIP_THEMES,
} from '@wg/wows-react-uikit/features/onboarding/components/Guidingtip/Guidingtip';

import { ONBOARDING_GUIDES_CONFIG } from '~/const/onboarding';
import { thousands } from '~/helpers/formatting';
import useMount from '~/hooks/useMount';
import { useRouteHandleSection } from '~/hooks/useRouteHandleSection';
import { buildNavItems } from '~/router/navigation/buildNavItems';
import { ROUTES_MAP, ROUTES_PROGRESS_TYPES } from '~/router/routes';
import routesConfig from '~/router/routesConfig';
import { useAppSelector } from '~/store';
import { navigationSelector } from '~/store/selectors/navigationSelectors';
import { removeOldLocalStorageObjects } from '~/utils/removeOldLocalStorageObjects';

import styles from './NavMenuLinks.module.scss';
import { NavMenuLinksBadge } from './NavMenuLinksBadge';

import type { ROUTES_BADGES } from '~/router/routes';

interface NavigationItem {
  key: string;
  to: string;
  end: boolean;
  replace: boolean;
  className: (isActive: boolean) => string;
  content: React.ReactNode;
  dataStaffName?: ROUTES_BADGES;
}

const useNavItems = () => {
  const { clanId } = useParams<{ clanId?: string }>();
  const { search } = useLocation();
  const { sections } = useRouteHandleSection();

  const navigationContext = useAppSelector(navigationSelector);

  useMount(removeOldLocalStorageObjects);

  const onRouteClick = useCallback(() => {
    void playButtonSound();
  }, []);

  const items = useMemo<NavigationItem[]>(() => {
    if (navigationContext.isClanHubEnabled && !sections) {
      return [];
    }

    const items = buildNavItems(
      {
        routes: routesConfig,
        sections: sections || [],
        clanId,
        search,
      },
      navigationContext,
    );

    return items.map((item) => {
      const isProgressLink = item.progressModel.kind !== ROUTES_PROGRESS_TYPES.NONE;

      // content
      let content: React.ReactNode = (
        <>
          {item.title}
          <NavMenuLinksBadge badge={item.badgeModel} />
        </>
      );

      // GuidingTips
      if (item.route.path === ROUTES_MAP.CLAN_STARS) {
        const guidingTip = ONBOARDING_GUIDES_CONFIG.CLANSTARS_REFINED;

        content = (
          <Guidingtip
            className={styles.guidingtip}
            name={guidingTip.name}
            header={guidingTip.header}
            content={guidingTip.content}
            position={GUIDINGTIP_POSITIONS.RIGHT}
            theme={GUIDINGTIP_THEMES.CLIENT}
            withBeacon={true}
            delay={1000}
          >
            {content}
          </Guidingtip>
        );
      }

      // Progress content
      if (item.progressModel.kind === ROUTES_PROGRESS_TYPES.PROFILE_SUPPLY) {
        const { completed, current, total } = item.progressModel;

        content = (
          <div>
            <div className={styles.title}>{item.title}</div>
            <ProgressBar size="small" completed={completed} isNavProgress />
            <div className={styles.progressText}>
              <div className={styles.navClanResource}>{thousands(current)}</div>
              &nbsp;/&nbsp;
              <div className={styles.navClanResource}>{thousands(total)}</div>
            </div>
          </div>
        );
      }

      return {
        key: item.key,
        to: item.to,
        end: item.end,
        replace: item.replace,
        dataStaffName: item.badge,
        content,
        className: (isActive) =>
          cx(styles.link, {
            [styles.link__progressLink]: isProgressLink,
            [styles.linkSpacer]: item.flags.separator,
            [styles.linkCurrent]: isActive,
            [styles.active]: item.flags.isAttention,
          }),
      };
    });
  }, [clanId, navigationContext, search, sections]);

  return {
    isBlured: navigationContext.clanWarsIsFromPort,
    items,
    onRouteClick,
  };
};

export default useNavItems;
